import {
  blankCertification,
  blankContactLink,
  blankEducation,
  blankExperience,
  blankProject,
  blankSkill,
  appendEntry,
  entryList,
  updateEntryAsImport,
  setContactFieldAsImport,
  setSummary,
  updateContactLinkAsImport,
} from './model';
import type {
  AnyEntry,
  CandidateDecision,
  CandidateFact,
  CandidatePayload,
  EntrySection,
  FactConflict,
  ImportSession,
  MasterProfile,
} from './types';
import { parseCvText } from './parse';
import { entrySignature, linkSignature, normalizeFact } from './model';

// normalize entry draft? import type
import type { EntryDraft } from './types';

export type MergeResult = {
  profile: MasterProfile;
  applied: string[];
  skipped: string[];
};

export function createImportSession(document: string, text: string): ImportSession {
  const outcome = parseCvText(text);
  const candidates = outcome.candidates;
  const decisions: Record<string, CandidateDecision> = {};
  const conflicts: Record<string, 'pending' | 'replace' | 'keep'> = {};
  for (const candidate of candidates) {
    decisions[candidate.id] = 'pending';
    conflicts[candidate.id] = 'pending';
  }
  // conflicts are computed against a profile later in UI; store initial against empty profile.
  // Recompute properly against current profile in detectConflict calls.
  return {
    document,
    text,
    candidates,
    decisions,
    conflicts,
    edits: {},
    unclassified: outcome.unclassified,
    notes: outcome.notes,
  };
}

export function payloadFor(session: ImportSession, candidate: CandidateFact): CandidatePayload {
  return session.edits[candidate.id] ?? candidate.payload;
}

export function detectConflict(profile: MasterProfile, candidate: CandidateFact, payload?: CandidatePayload): FactConflict | null {
  const p = payload ?? candidate.payload;
  if (p.kind === 'contact-field') {
    const field = p.field;
    const existing = profile.contact[field].value;
    const proposed = p.value;
    if (existing.trim().length > 0 && normalizeFact(existing) !== normalizeFact(proposed)) {
      return { kind: 'different-value', existing, proposed, confirmed: profile.contact[field].state === 'confirmed' };
    }
    return null;
  }
  if (p.kind === 'contact-link') {
    const existingLink = profile.contact.links.find((link) => linkSignature(link) === normalizeFact(p.url));
    if (existingLink) {
      if (normalizeFact(existingLink.label) !== normalizeFact(p.label) || normalizeFact(existingLink.url) !== normalizeFact(p.url)) {
        return { kind: 'duplicate-entry', existing: `${existingLink.label}: ${existingLink.url}`, proposed: `${p.label}: ${p.url}`, confirmed: existingLink.state === 'confirmed' };
      }
    }
    return null;
  }
  if (p.kind === 'summary') {
    const existing = profile.summary.value;
    const proposed = p.value;
    if (existing.trim().length > 0 && normalizeFact(existing) !== normalizeFact(proposed)) {
      return { kind: 'different-value', existing, proposed, confirmed: profile.summary.state === 'confirmed' };
    }
    return null;
  }
  if (p.kind === 'experience' || p.kind === 'education' || p.kind === 'skill' || p.kind === 'certification' || p.kind === 'project') {
    const sec = sectionForPayload(p.kind);
    const draft = p.entry as EntryDraft<EntrySection>;
    const proposedSig = entrySignatureForDraft(sec, draft as unknown as AnyEntry);
    const existingEntry = (entryList(profile, sec) as AnyEntry[]).find((e) => entrySignature(e) === proposedSig);
    if (existingEntry) {
      const existingText = stringifyEntry(existingEntry);
      const proposedText = stringifyDraft(draft);
      if (normalizeFact(existingText) !== normalizeFact(proposedText)) {
        return { kind: 'duplicate-entry', existing: existingText, proposed: proposedText, confirmed: existingEntry.state === 'confirmed' };
      }
      // identical: still flag as duplicate so it is not silently duplicated
      return { kind: 'duplicate-entry', existing: existingText, proposed: proposedText, confirmed: existingEntry.state === 'confirmed' };
    }
  }
  return null;
}

function sectionForPayload(kind: CandidatePayload['kind']): EntrySection {
  if (kind === 'experience') return 'experience';
  if (kind === 'education') return 'education';
  if (kind === 'skill') return 'skills';
  if (kind === 'certification') return 'certifications';
  return 'projects';
}

function entrySignatureForDraft(section: EntrySection, draft: AnyEntry): string {
  if (section === 'experience') {
    const d = draft as unknown as { organization: string; role: string };
    return `${normalizeFact(d.organization)}|${normalizeFact(d.role)}`;
  }
  if (section === 'education') {
    const d = draft as unknown as { institution: string; credential: string };
    return `${normalizeFact(d.institution)}|${normalizeFact(d.credential)}`;
  }
  if (section === 'skills') {
    return normalizeFact((draft as { name: string }).name);
  }
  if (section === 'certifications') {
    return normalizeFact((draft as { name: string }).name);
  }
  return normalizeFact((draft as { name: string }).name);
}

function stringifyEntry(entry: AnyEntry): string {
  return JSON.stringify(entry);
}

function stringifyDraft(draft: EntryDraft<EntrySection>): string {
  return JSON.stringify(draft);
}

export function isResolved(session: ImportSession, candidateId: string, profile: MasterProfile): boolean {
  const candidate = session.candidates.find((c) => c.id === candidateId);
  if (!candidate) return false;
  const conflict = detectConflict(profile, candidate, payloadFor(session, candidate));
  if (!conflict) return true;
  const choice = session.conflicts[candidateId] ?? 'pending';
  return choice === 'replace' || choice === 'keep';
}

export function applyCandidate(profile: MasterProfile, session: ImportSession, candidateId: string): { profile: MasterProfile; applied: boolean } {
  const candidate = session.candidates.find((c) => c.id === candidateId);
  if (!candidate) return { profile, applied: false };
  const decision = session.decisions[candidateId];
  if (decision !== 'accepted') return { profile, applied: false };
  const payload = payloadFor(session, candidate);
  const conflict = detectConflict(profile, candidate, payload);
  if (conflict) {
    const choice = session.conflicts[candidateId] ?? 'pending';
    if (choice !== 'replace' && choice !== 'keep') return { profile, applied: false };
    if (choice === 'keep') return { profile, applied: false };
  }
  return { profile: applyPayload(profile, payload, candidate.evidence), applied: true };
}

export function applyAccepted(profile: MasterProfile, session: ImportSession): MergeResult {
  let next = profile;
  const applied: string[] = [];
  const skipped: string[] = [];
  for (const candidate of session.candidates) {
    if (session.decisions[candidate.id] !== 'accepted') { skipped.push(candidate.id); continue; }
    const result = applyCandidate(next, session, candidate.id);
    if (result.applied) { next = result.profile; applied.push(candidate.id); }
    else skipped.push(candidate.id);
  }
  return { profile: next, applied, skipped };
}

function applyPayload(profile: MasterProfile, payload: CandidatePayload, evidence: string): MasterProfile {
  switch (payload.kind) {
    case 'contact-field': {
      if (payload.field === 'email') return setContactFieldAsImport(profile, 'email', payload.value) as MasterProfile;
      if (payload.field === 'phone') return setContactFieldAsImport(profile, 'phone', payload.value);
      if (payload.field === 'location') return setContactFieldAsImport(profile, 'location', payload.value);
      return setContactFieldAsImport(profile, 'fullName', payload.value);
    }
    case 'contact-link': {
      const existing = profile.contact.links.find((l) => linkSignature(l) === normalizeFact(payload.url));
      if (existing) {
        return updateContactLinkAsImport(profile, existing.id, { label: payload.label, url: payload.url });
      }
      const link = { ...blankContactLink(), label: payload.label, url: payload.url, evidence, state: 'needs-review' as const };
      return { ...profile, contact: { ...profile.contact, links: [...profile.contact.links, link] } };
    }
    case 'summary':
      return setSummary(profile, payload.value, true);
    case 'experience': {
      const draft = payload.entry;
      const entry = { ...blankExperience(), ...draft, evidence, state: 'needs-review' as const };
      const existing = (entryList(profile, 'experience') as AnyEntry[]).find((e) => entrySignature(e) === `${normalizeFact(entry.organization)}|${normalizeFact(entry.role)}`);
      if (existing) {
        return updateEntryAsImport(profile, 'experience', existing.id, { ...draft, evidence } as never);
      }
      return appendEntry(profile, 'experience', entry as never);
    }
    case 'education': {
      const draft = payload.entry;
      const entry = { ...blankEducation(), ...draft, evidence, state: 'needs-review' as const };
      const existing = (entryList(profile, 'education') as AnyEntry[]).find((e) => entrySignature(e) === `${normalizeFact(entry.institution)}|${normalizeFact(entry.credential)}`);
      if (existing) {
        return updateEntryAsImport(profile, 'education', existing.id, { ...draft, evidence } as never);
      }
      return appendEntry(profile, 'education', entry as never);
    }
    case 'skill': {
      const draft = payload.entry;
      const entry = { ...blankSkill(), ...draft, evidence, state: 'needs-review' as const };
      const existing = (entryList(profile, 'skills') as AnyEntry[]).find((e) => entrySignature(e) === normalizeFact(entry.name));
      if (existing) {
        return updateEntryAsImport(profile, 'skills' as never, existing.id, { ...draft, evidence } as never);
      }
      return appendEntry(profile, 'skills', entry as never);
    }
    case 'certification': {
      const draft = payload.entry;
      const entry = { ...blankCertification(), ...draft, evidence, state: 'needs-review' as const };
      const existing = (entryList(profile, 'certifications') as AnyEntry[]).find((e) => entrySignature(e) === normalizeFact(entry.name));
      if (existing) {
        return updateEntryAsImport(profile, 'certifications' as never, existing.id, { ...draft, evidence } as never);
      }
      return appendEntry(profile, 'certifications', entry as never);
    }
    case 'project': {
      const draft = payload.entry;
      const entry = { ...blankProject(), ...draft, evidence, state: 'needs-review' as const };
      const existing = (entryList(profile, 'projects') as AnyEntry[]).find((e) => entrySignature(e) === normalizeFact(entry.name));
      if (existing) {
        return updateEntryAsImport(profile, 'projects' as never, existing.id, { ...draft, evidence } as never);
      }
      return appendEntry(profile, 'projects', entry as never);
    }
  }
}

